import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { MapPin, Phone } from 'lucide-react';
import { useData } from '../context/DataContext';
import { useCountry } from '../context/CountryContext';

const ClinicsDirectory = () => {
  const navigate = useNavigate();
  const { clinics, specialties, countries, loading } = useData();
  const { detectedCountry } = useCountry();
  
  if (loading || !clinics) {
    return (
      <div className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-500 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }
  
  // Filter clinics by country and get first 6 for display
  const filteredClinics = clinics.filter(clinic => 
    !detectedCountry || clinic.country === detectedCountry
  );
  const displayClinics = filteredClinics.slice(0, 6);
  
  const countryInfo = countries?.find(c => c.code === detectedCountry);

  const getSpecialtyName = (id) => {
    if (!specialties) return id;
    const specialty = specialties.find(s => s.id === id);
    return specialty ? specialty.name : id;
  };

  return (
    <div className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h2 className="text-4xl font-bold text-gray-900 mb-2">Directorio de Clínicas</h2>
            <p className="text-gray-600">
              {countryInfo 
                ? `Clínicas dentales en ${countryInfo.flag} ${countryInfo.name}` 
                : 'Encuentra la clínica dental perfecta para tus necesidades'}
            </p>
          </div>
          <Button 
            variant="outline" 
            className="border-cyan-500 text-cyan-600 hover:bg-cyan-50"
            onClick={() => navigate('/clinicas')}
          >
            Ver todas las clínicas
          </Button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayClinics.map((clinic) => (
            <Card 
              key={clinic.id} 
              className="group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden border-gray-200"
              onClick={() => navigate(`/clinicas/${clinic.id}`)}
            >
              <div className="relative overflow-hidden h-48">
                <img
                  src={clinic.image}
                  alt={clinic.name}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                />
              </div>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-cyan-600 transition-colors">
                  {clinic.name}
                </h3>
                <div className="flex items-center text-gray-600 mb-2">
                  <MapPin className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="text-sm">{clinic.city}</span>
                  {clinic.branches && clinic.branches.length > 1 && (
                    <Badge variant="outline" className="ml-2 text-xs border-cyan-500 text-cyan-600">
                      {clinic.branches.length} sucursales
                    </Badge>
                  )}
                </div>
                <div className="flex items-center text-gray-600 mb-4">
                  <Phone className="h-4 w-4 mr-1 flex-shrink-0" />
                  <span className="text-sm">{clinic.phone}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {clinic.specialties.slice(0, 3).map((spec, idx) => (
                    <Badge key={idx} variant="secondary" className="bg-cyan-100 text-cyan-700 text-xs">
                      {getSpecialtyName(spec)}
                    </Badge>
                  ))}
                  {clinic.specialties.length > 3 && (
                    <Badge variant="secondary" className="bg-gray-100 text-gray-700 text-xs">
                      +{clinic.specialties.length - 3} más
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
};

export default ClinicsDirectory;
